import { useEffect, useState } from 'react';

/**
 * Reports which of a set of sections is currently being read.
 *
 * The band is deliberately narrow — a slice across the upper-middle of the
 * viewport rather than the whole of it. With a full-height root, two or
 * three long sections are on screen at once and "active" becomes a
 * coin toss that flickers as you scroll. Against a thin band, exactly one
 * section is crossing at a time and the answer is stable.
 *
 * Elements are looked up from a container rather than collected through
 * refs, because each step already owns its ref for its arrival animation
 * and a component cannot carry two.
 *
 * @param {React.RefObject<HTMLElement>} containerRef element holding the sections
 * @param {string} selector how to find them inside it
 * @param {number} count how many there are, so a change re-runs the query
 * @returns {number} index of the active section
 */
export function useActiveStep(containerRef, selector, count) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const root = containerRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;

    const sections = Array.from(root.querySelectorAll(selector));
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const index = sections.indexOf(entry.target);
            if (index !== -1) setActive(index);
          }
        }
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [containerRef, selector, count]);

  return active;
}
